import jsPDF from 'jspdf';
import { Barcode } from './barcode';
import { PAPER_TYPES } from './constants';
import { FormValues } from './models/form-values';
import { Callback } from './types/callback.type';
import { ResultCallback } from './types/result-callback.type';
import { calculateNumberOfRowsPerPage } from './utils/calculate-number-of-rows-per-page.fn';
import { partition } from './utils/partition.fn';
import { transformToPaperOrientation } from './utils/transform-to-paper-orientation.fn';
import { transformToPaperType } from './utils/transform-to-paper-type.fn';

export class PdfRenderer {
  constructor(private barcodes: Barcode[]) {}

  async render(
    formValues: FormValues,
    onBarcodeRendered: Callback,
    onPageFinished: ResultCallback<{
      page: number;
      total: number;
    }>,
    onComplete: Callback,
  ) {
    const paperOrientation = transformToPaperOrientation(formValues.paperType);
    const paperType = transformToPaperType(formValues.paperType);
    const paperDimensions = PAPER_TYPES[formValues.paperType];

    const doc = new jsPDF({
      orientation: paperOrientation,
      unit: 'mm',
      format: paperType,
    });

    const barcodeColumns = partition(this.barcodes, formValues.numberOfColumns);
    const maxRowsOfLabelsPerPage = calculateNumberOfRowsPerPage(
      paperDimensions,
      formValues.paperTopMargin,
      formValues.paperBottomMargin,
      formValues.labelHeight,
      formValues.labelGutterHeight,
    );
    const barcodePages = partition(barcodeColumns, maxRowsOfLabelsPerPage);
    let currentPage = 0;

    for (const barcodeRows of barcodePages) {
      doc.setPage(currentPage + 1);
      let currentRow = 0;

      for (const barcodeColumns of barcodeRows) {
        let currentColumn = 0;

        for (const barcode of barcodeColumns) {
          await new Promise<void>(resolve =>
            requestAnimationFrame(() => {
              const x =
                formValues.paperLeftMargin +
                formValues.labelGutterWidth * currentColumn +
                formValues.labelWidth * currentColumn;
              const y =
                formValues.paperTopMargin +
                formValues.labelGutterHeight * currentRow +
                formValues.labelHeight * currentRow;

              doc.addImage(
                barcode.dataUrl,
                'PNG',
                x,
                y,
                formValues.labelWidth,
                formValues.labelHeight,
                `barcode-${barcode.value}`,
                formValues.compressionRate,
                0,
              );

              onBarcodeRendered();
              resolve();
            }),
          );

          currentColumn++;
        }

        currentRow++;
      }

      onPageFinished({
        page: currentPage,
        total: barcodePages.length,
      });

      if (currentPage < barcodePages.length - 1) {
        doc.addPage();
      }

      currentPage++;
    }

    onComplete();

    await doc.save('barcodes.pdf', { returnPromise: true });
  }
}
